import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Paperclip, X, Loader2,
  FileText, Image, Film, Briefcase, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import { servicesService } from '../../services/ServicesService';

const ServiceRequestForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const preselectedType = searchParams.get('type') || '';

  const [formData, setFormData] = useState({
    service_type: preselectedType,
    phone: '',
    contact_email: currentUser?.email || '',
    observations: '',
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const serviceTypes = servicesService.helpers.getServiceTypes();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleFileRemove = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type.startsWith('video/')) return Film;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.service_type) {
      setError('Selecione um tipo de serviço');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Informe um telefone para contato');
      return;
    }
    if (!formData.contact_email.trim()) {
      setError('Informe um email para contato');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append('service_type', formData.service_type);
      data.append('phone', formData.phone.trim());
      data.append('contact_email', formData.contact_email.trim());
      data.append('observations', formData.observations.trim());

      files.forEach(file => {
        data.append('files', file);
      });

      await servicesService.createTicket(data);
      navigate('/services');
    } catch (err) {
      console.error('Erro ao criar ticket:', err);
      setError(err.response?.data?.detail || 'Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => navigate('/services')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Voltar</span>
              </button>
              <h1 className="text-lg font-bold text-gray-900">Solicitar Serviço</h1>
              <div className="w-20" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 lg:p-8">

          {/* Service Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tipo de Serviço *
            </label>
            <select
              name="service_type"
              value={formData.service_type}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all"
            >
              <option value="">Selecione um serviço...</option>
              {serviceTypes.map(st => (
                <option key={st.value} value={st.value}>{st.label}</option>
              ))}
            </select>
          </div>

          {/* Phone */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Telefone para Contato *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(11) 99999-9999"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all"
            />
          </div>

          {/* Email */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email para Contato *
            </label>
            <input
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              placeholder="seu@email.com"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all"
            />
            <p className="text-xs text-gray-400 mt-1">Pré-preenchido com seu email de login. Edite se preferir outro.</p>
          </div>

          {/* Observations */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              name="observations"
              value={formData.observations}
              onChange={handleChange}
              rows={5}
              maxLength={1000}
              placeholder="Descreva o que você precisa, detalhes do projeto, referências, prazos..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all"
            />
            <p className="text-xs text-gray-400 mt-1">{formData.observations.length}/1000 caracteres</p>
          </div>

          {/* File Upload */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Anexos
            </label>

            <label className="flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50/50 transition-all cursor-pointer">
              <Paperclip className="w-5 h-5" />
              <span className="font-medium">Clique para anexar arquivos</span>
              <input
                type="file"
                multiple
                onChange={handleFileAdd}
                className="hidden"
              />
            </label>

            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((file, index) => {
                  const FileIcon = getFileIcon(file);
                  return (
                    <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <FileIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                        <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(index)}
                        className="p-1 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-bold text-lg rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Enviar Solicitação
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ServiceRequestForm;
